import { useState, useCallback, useRef } from 'react';
import { Upload, FileText, X, Loader2 } from 'lucide-react';

interface PdfUploaderProps {
    onUpload: (base64: string, fileName: string) => void;
    isProcessing: boolean;
}

export function PdfUploader({ onUpload, isProcessing }: PdfUploaderProps) {
    const [dragOver, setDragOver] = useState(false);
    const [selectedFile, setSelectedFile] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const processFile = useCallback((file: File) => {
        if (file.type !== 'application/pdf') {
            return;
        }
        if (file.size > 20 * 1024 * 1024) {
            return;
        }

        setSelectedFile(file.name);

        const reader = new FileReader();
        reader.onload = () => {
            const base64 = (reader.result as string).split(',')[1];
            onUpload(base64, file.name);
        };
        reader.readAsDataURL(file);
    }, [onUpload]);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file) processFile(file);
    }, [processFile]);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(true);
    }, []);

    const handleDragLeave = useCallback(() => {
        setDragOver(false);
    }, []);

    const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) processFile(file);
    }, [processFile]);

    const handleClear = useCallback(() => {
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    }, []);

    return (
        <div className="space-y-3">
            {!selectedFile ? (
                <div
                    className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                        dragOver
                            ? 'border-blue-400 bg-blue-50'
                            : 'border-gray-300 hover:border-blue-300 hover:bg-gray-50'
                    }`}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <Upload className="w-10 h-10 mx-auto text-gray-400 mb-3" />
                    <p className="text-sm font-medium text-gray-700">
                        Drop your MSDS PDF here or click to select
                    </p>
                    <p className="text-xs text-gray-500 mt-1">PDF files up to 20MB</p>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf"
                        className="hidden"
                        onChange={handleFileSelect}
                    />
                </div>
            ) : (
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border">
                    <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <span className="text-sm font-medium text-gray-700 truncate flex-1">
                        {selectedFile}
                    </span>
                    {isProcessing ? (
                        <div className="flex items-center gap-2 text-sm text-blue-600">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span>Extracting data...</span>
                        </div>
                    ) : (
                        <button
                            onClick={handleClear}
                            className="p-1 hover:bg-gray-200 rounded"
                            title="Remove file"
                        >
                            <X className="w-4 h-4 text-gray-500" />
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
